import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { apiClient } from "@/lib/api";
import { VoraLogo } from "@/components/brand/VoraLogo";
import { Mail, Lock, Loader2, ArrowRight } from "lucide-react";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  const redirectTo = location.state?.from?.pathname || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiClient.post('/auth/login', formData);
      login(response.data);

      // If opened from embedded preview, notify parent and prompt return
      if (window.opener) {
        try { window.opener.postMessage({ type: "vora_auth_success" }, window.location.origin); } catch {}
        toast.success("Logged in! You can close this tab and return to the preview.");
        return;
      }

      toast.success("Welcome back!");
      navigate(response.data.onboarding_completed ? redirectTo : "/onboarding");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="vora-auth-page" data-testid="login-page">
      {/* Subtle grid background */}
      <div className="vora-auth-grid" />

      {/* Top nav */}
      <header className="vora-auth-header">
        <Link to="/" className="flex items-center gap-2" data-testid="auth-logo-link">
          <VoraLogo variant="icon" size={22} />
          <span className="text-base font-bold font-heading tracking-tight" style={{ color: "#111111" }}>VORA</span>
        </Link>
        <Link
          to="/signup"
          className="text-sm font-medium transition-colors"
          style={{ color: "#6B7280" }}
          data-testid="header-signup-link"
          onMouseEnter={e => e.currentTarget.style.color = "#111111"}
          onMouseLeave={e => e.currentTarget.style.color = "#6B7280"}
        >
          Create account
        </Link>
      </header>

      {/* Center card */}
      <main className="vora-auth-main">
        <div className="vora-auth-card" data-testid="login-card">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold font-heading mb-2" style={{ color: "#111111" }}>Welcome back</h1>
            <p className="text-sm" style={{ color: "#5F6368" }}>Track smarter, eat better</p>
          </div>

          {/* Google */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            className="vora-auth-google-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>Continue with Google</span>
          </button>

          {/* Divider */}
          <div className="vora-auth-divider">
            <div className="vora-auth-divider-line" />
            <span className="vora-auth-divider-text">or</span>
            <div className="vora-auth-divider-line" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="vora-auth-label">Email</label>
              <div className="relative">
                <Mail className="vora-auth-input-icon" />
                <input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="email-input"
                  className="vora-auth-input"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="vora-auth-label">Password</label>
              <div className="relative">
                <Lock className="vora-auth-input-icon" />
                <input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="password-input"
                  className="vora-auth-input"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="vora-auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>Log in <ArrowRight className="w-4 h-4 ml-1" /></>
              )}
            </button>
          </form>

          <p className="text-center text-sm mt-6" style={{ color: "#5F6368" }}>
            Don't have an account?{" "}
            <Link to="/signup" className="font-medium hover:underline" style={{ color: "#4E7A67" }} data-testid="signup-link">
              Sign up
            </Link>
          </p>
        </div>

        <p className="text-center text-xs mt-8" style={{ color: "#8B9096" }}>
          By continuing, you agree to our Terms and Privacy Policy.
        </p>
      </main>
    </div>
  );
};

export default LoginPage;
